import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Branch } from "../model/branches";
import { Messages } from "../model/messages";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { EnvironmentHelper } from "./env.helper";
import { NavigationHelper } from "./navigation.helper";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class BranchDetailsService {
  private apiUrl: string;
  private postUrl: string;
  private _currentBranche: BehaviorSubject<Branch>;

  constructor(
    private nav: NavigationHelper,
    private http: HttpClient,
    private router: Router,
    private auth: AuthService,
    private env: EnvironmentHelper,
    private translate: TranslateService
  ) {
    this.apiUrl = env.apiBaseUrl + "branch/by-id/";
    this.postUrl = env.apiBaseUrl + "branch/";
    this._currentBranche = new BehaviorSubject(Branch.EMPTY);
  }

  public createBranch(token: string, branch: Branch): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.postUrl, branch, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveBranchDetails(response, observer),
          (error) => this.receiveBranchDetails(error, observer)
        );
    });
  }

  public updateBranch(token: string, branch: Branch): Observable<Messages> {
    const branchUrl = `${this.apiUrl}${branch.id}`;

    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(branchUrl, branch, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveBranchDetails(response, observer),
          (error) => this.receiveBranchDetails(error, observer)
        );
    });
  }

  public deleteBranch(token: string, branchId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(`${this.apiUrl}${branchId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveBranchDetails(response, observer),
          (error) => this.receiveBranchDetails(error, observer)
        );
    });
  }

  public getBranchById(token: string, branchId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.apiUrl}${branchId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveBranchDetails(response, observer),
          (error) => this.receiveBranchDetails(error, observer)
        );
    });
  }

  private receiveBranchDetails(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Branch>)?.body;

      if (!data) {
        observer.next(
          Messages.from({
            branches: [this.translate.instant("branches.unknown")],
          })
        );
        return;
      }

      this._currentBranche.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 204) {
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "account",
          "login.not.authorized"
        )
      );
    } else if (response.status === 400) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "branch",
          "branch.wrong.data"
        )
      );
    } else if (response.status === 409) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "branch",
          "branch.conflict.error"
        )
      );
    } else if (response.status === 500) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "branch",
          "branch.internal.error"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "branch",
          "branch.technical.error." + response.status
        )
      );
    }
  }

  public get branchObservable(): Observable<Branch> {
    return this._currentBranche;
  }

  public select(branch: Branch) {
    this._currentBranche.next(branch);
  }

  public selectFromRouter() {
    const navigation = this.nav.current;

    if (navigation.id && navigation.id.length > 0) {
      const branchId = Number(navigation.id);
      const token = this.auth.token;

      this.getBranchById(token, branchId).subscribe((messages) => {});
    } else {
      const emptyBranch = new Branch(0, "");
      this.select(emptyBranch);
    }
  }

  public get selected(): Branch {
    return this._currentBranche.value;
  }
}
